import dayjs from 'utils/dayjs';
import { get, set } from 'lodash';
import { buildObjectFromKeys, sumObjectsProperties } from 'utils/objects';
import { ROOM_KEYS, REVENUE_KEYS } from 'utils/analytics/types';

/**
 * Merge multiple API state together
 * This is a simple algorithm that returns (in order):
 * - an error if any of the APIs failed
 * - fetching, is any of the APIs is still fetching
 * - fetched, is all of the APIs  finished successfully
 * @param  {Object[]} states
 * @param  {Boolean}  states[].fetching
 * @param  {Object}   states[].error
 * @param  {Boolean}  states[].fetched
 * @return {Object} the final merged state
 */
const mergeStates = (states) =>
    states.reduce(
        (mergedState, state) => {
            const error = mergedState.error || state.error;
            if (error)
                return {
                    error,
                    fetching: false,
                    fetched: false,
                };
            if (mergedState.fetching || state.fetching)
                return {
                    fetching: true,
                    error: null,
                    fetched: false,
                };
            return {
                fetching: false,
                error: null,
                fetched: mergedState.fetched && state.fetched,
            };
        },
        {
            fetching: false,
            error: null,
            fetched: true,
        },
    );

/**
 * Cumulates all rooms (by status) in the given array of data
 * Ignore other fields are they not required
 * @param  {Object[]} data
 * @return {Object} an object with the rooms & revenues totals
 */
const calculateTotals = (data) => {
    const keysToSum = [...ROOM_KEYS, ...REVENUE_KEYS];

    return data?.reduce((totals, row) => sumObjectsProperties(totals, row, keysToSum), buildObjectFromKeys(keysToSum));
};

/**
 * Checks if there is data to show
 * @param {Object} totals - the processed data (totals)
 * @return {Boolean} if there is data
 */
const isDatasetEmpty = ({ current, lySpit, lyFinal }) =>
    current.rooms.guaranteed === 0 &&
    current.rooms.nonGuaranteed === 0 &&
    lySpit.rooms.guaranteed === 0 &&
    lySpit.rooms.nonGuaranteed === 0 &&
    lyFinal.rooms.guaranteed === 0 &&
    lyFinal.rooms.nonGuaranteed === 0;

/**
 * Calculate the variance vs LySpit and  vs LyFinal and add it to the input
 * @param {Object} totals - an object with current, lyFinal & lySpit rooms & revenue totals
 * @return {Object} The totals with 2 extra proeprties: lySpitVariance, lyFinalVariance
 */
const calculateLastYearVariances = (totals) => {
    const keysToCalculate = [...ROOM_KEYS, ...REVENUE_KEYS];
    return [
        { mergeKey: 'lySpitVariance', diffKey: 'lySpit' },
        { mergeKey: 'lyFinalVariance', diffKey: 'lyFinal' },
        { mergeKey: 'y2SpitVariance', diffKey: 'y2Spit' },
        { mergeKey: 'y2FinalVariance', diffKey: 'y2Final' },
    ].reduce(
        (accu, { mergeKey, diffKey }) => ({
            ...accu,
            [mergeKey]: keysToCalculate.reduce((accu2, key) => {
                const diff = get(totals, `current.${key}`) - get(totals, `${diffKey}.${key}`);
                return set({ ...accu2 }, key, diff);
            }, {}),
        }),
        {},
    );
};

/**
 * Filters the data to only keep date matching a specific range
 * @param {Object[]} data
 * @param {Object} dateFilters - the range of date to keep from the data
 * @param {String} dateFilters.startDate
 * @param {String} dateFilters.endDate
 * @return {Object[]}
 */
const filterDatesInRange = (data, { startDate, endDate }) =>
    data.filter(
        ({ stayDate }) =>
            dayjs(stayDate).isSameOrAfter(dayjs(startDate)) && dayjs(stayDate).isSameOrBefore(dayjs(endDate)),
    );

export { mergeStates, calculateTotals, isDatasetEmpty, calculateLastYearVariances, filterDatesInRange };
